// =============================================================================
// ConfirmDialog — themed replacement for window.confirm()
// =============================================================================
// A small, accessible confirmation modal built on top of <Modal>. The
// `useConfirm` hook keeps call sites nearly identical to the native API:
//
//   const { confirm, dialog } = useConfirm();
//   // ...
//   if (await confirm({ title: 'Delete?', tone: 'danger' })) doDelete();
//   // ...render {dialog} once anywhere in the component tree
//
// The promise resolves `true` on confirm and `false` on cancel / backdrop /
// Escape, so existing imperative `if (confirm) {}` flows port directly.
// =============================================================================

import { useCallback, useRef, useState, type ReactElement } from 'react';

import { Button } from './Button';
import { Modal, ModalFooter } from './Modal';

export interface ConfirmOptions {
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` renders a red confirm button for destructive actions. */
  tone?: 'default' | 'danger';
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      size="sm"
    >
      <ModalFooter>
        <Button variant="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          variant={tone === 'danger' ? 'danger' : 'primary'}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

ConfirmDialog.displayName = 'ConfirmDialog';

export interface UseConfirmResult {
  /** Open the dialog; resolves true on confirm, false otherwise. */
  confirm: (options?: ConfirmOptions) => Promise<boolean>;
  /** Render this once in the component's JSX. */
  dialog: ReactElement;
}

export function useConfirm(): UseConfirmResult {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({});
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpen(false);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions = {}) => {
    // Resolve any prior, still-open prompt as cancelled before reopening.
    resolverRef.current?.(false);
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const dialog = (
    <ConfirmDialog
      open={open}
      {...options}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, dialog };
}
